import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "../utils/cx";

export interface CaptureModeOption<Mode extends string> {
  readonly id: Mode;
  readonly label: string;
  readonly icon?: ReactNode;
}

export interface CaptureModeSelectorProps<Mode extends string> {
  readonly value: Mode;
  readonly options: readonly CaptureModeOption<Mode>[];
  readonly onValueChange: (value: Mode) => void;
  readonly label: string;
  readonly disabled?: boolean;
  readonly className?: string;
}

export function CaptureModeSelector<Mode extends string>({
  value,
  options,
  onValueChange,
  label,
  disabled = false,
  className,
}: CaptureModeSelectorProps<Mode>) {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectAt = (index: number) => {
    const option = options[index];
    if (!option || disabled) return;
    onValueChange(option.id);
    buttonRefs.current[index]?.focus();
  };
  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const lastIndex = options.length - 1;
    const nextIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? lastIndex
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? index === 0 ? lastIndex : index - 1
          : index === lastIndex ? 0 : index + 1;
    selectAt(nextIndex);
  };

  return (
    <div
      className={cx("ui-capture-mode-selector", className)}
      role="radiogroup"
      aria-label={label}
    >
      {options.map((option, index) => (
        <button
          key={option.id}
          ref={(element) => {
            buttonRefs.current[index] = element;
          }}
          type="button"
          role="radio"
          aria-checked={value === option.id}
          tabIndex={value === option.id ? 0 : -1}
          disabled={disabled}
          onClick={() => onValueChange(option.id)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        >
          {option.icon ? (
            <span className="ui-capture-mode-selector__icon" aria-hidden="true">
              {option.icon}
            </span>
          ) : null}
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}
